import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useMusAction,
  useMusConversation,
  useMusOverview,
  useMusQuestions,
  useMyMus,
  useUsers
} from '../api/queries'
import type { MusConversation, MusQuestion, MusSide, MusStatus, MusSummary } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { useCreateRequest } from '../lib/createRequest'
import { formatDate, formatDateTime } from '../lib/format'
import { Card, EmptyState, ErrorBox, Loading } from './ui'

/**
 * MUS-samtaler (docs/DECISIONS.md beslutning 129).
 *
 * <b>Fortroligt</b>: forberedelse, referat og aftaler ser KUN medarbejderen og lederen. Kontoret ser kun hvem,
 * hvornår og status - serveren sender aldrig mere til andre end de to.
 */

const LEADER_ROLES = ['Superadmin', 'Administrator', 'Kontor', 'Projektleder']

const STATUS: Record<MusStatus, string> = {
  planned: 'Planlagt',
  awaiting_approval: 'Venter på godkendelse af referatet',
  approved: 'Holdt og godkendt',
  cancelled: 'Aflyst'
}

const errorText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback)

function toLocalInput(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ---------------------------------------------------------------------------
// Fanen
// ---------------------------------------------------------------------------

export function MusTab() {
  const { isProjectManager, isIntranetAdmin, user } = useAuth()
  const mine = useMyMus()
  const [planning, setPlanning] = useState<{ employeeId?: number } | null>(null)

  useCreateRequest(['mus'], () => {
    if (isProjectManager) setPlanning({})
  })

  if (mine.isLoading) return <Loading />
  if (mine.error) return <ErrorBox error={mine.error} />

  const list = mine.data ?? []
  const open = list.filter((c) => c.status === 'planned' || c.status === 'awaiting_approval')
  const done = list.filter((c) => c.status === 'approved' || c.status === 'cancelled')

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="tiny muted">
        🔒 En MUS-samtale er fortrolig: forberedelse, referat og aftaler ser kun du og den anden fra samtalen.
      </div>

      {isProjectManager && !planning && (
        <div>
          <button className="btn btn--primary" onClick={() => setPlanning({})}>
            💬 Planlæg MUS-samtale
          </button>
        </div>
      )}
      {planning && <PlanForm employeeId={planning.employeeId} onClose={() => setPlanning(null)} />}

      <Card title="Mine samtaler">
        {open.length === 0 ? (
          <EmptyState>Du har ingen MUS-samtaler på vej.</EmptyState>
        ) : (
          <SummaryList items={open} meId={user?.id} />
        )}
      </Card>

      {isIntranetAdmin && <OverviewCard onPlan={(employeeId) => setPlanning({ employeeId })} />}
      {isIntranetAdmin && <QuestionsCard />}

      {done.length > 0 && (
        <Card title="Tidligere samtaler">
          <SummaryList items={done} meId={user?.id} />
        </Card>
      )}
    </div>
  )
}

function SummaryList({ items, meId }: { items: MusSummary[]; meId?: number }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      {items.map((c) => {
        const other = c.employee.id === meId ? c.leader : c.employee
        return (
          <Link
            key={c.id}
            to={`/intranet/mus/${c.id}`}
            className="row row--between"
            style={{ gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border, #E2E8F0)' }}
          >
            <div style={{ minWidth: 0 }}>
              <div className="small">
                <strong>MUS med {other.displayName}</strong>
              </div>
              <div className="tiny muted">
                {formatDateTime(c.scheduledAt)}
                {c.location ? ` · ${c.location}` : ''}
              </div>
            </div>
            <span className="tiny muted" style={{ textAlign: 'right' }}>
              {STATUS[c.status]}
            </span>
          </Link>
        )
      })}
    </div>
  )
}

function PlanForm({ employeeId, onClose }: { employeeId?: number; onClose: () => void }) {
  const { isIntranetAdmin, user } = useAuth()
  const { data: users } = useUsers()
  const action = useMusAction()
  const [employee, setEmployee] = useState(employeeId ? String(employeeId) : '')
  const [leader, setLeader] = useState(isIntranetAdmin ? '' : String(user?.id ?? ''))
  const [at, setAt] = useState('')
  const [location, setLocation] = useState('')
  const [error, setError] = useState<string | null>(null)

  const people = (users ?? []).filter((u) => u.isActive && !u.isHidden)
  const leaders = people.filter((u) => u.roles.some((r) => LEADER_ROLES.includes(r)))

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    if (!employee || !leader || !at) {
      setError('Vælg medarbejder, leder og tidspunkt.')
      return
    }
    try {
      await action.mutateAsync({
        path: '',
        body: {
          employeeUserId: Number(employee),
          leaderUserId: Number(leader),
          scheduledAt: new Date(at).toISOString(),
          location: location.trim() || null
        }
      })
      onClose()
    } catch (err) {
      setError(errorText(err, 'Samtalen kunne ikke planlægges.'))
    }
  }

  return (
    <Card title="Planlæg MUS-samtale">
      <form className="stack" style={{ gap: 10 }} onSubmit={(e) => void submit(e)}>
        <label className="small">
          Medarbejder
          <select value={employee} onChange={(e) => setEmployee(e.target.value)} required>
            <option value="">Vælg ...</option>
            {people.filter((u) => String(u.id) !== leader).map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </label>
        <label className="small">
          Leder (holder samtalen)
          <select value={leader} onChange={(e) => setLeader(e.target.value)} disabled={!isIntranetAdmin} required>
            <option value="">Vælg ...</option>
            {(isIntranetAdmin ? leaders : people.filter((u) => u.id === user?.id)).map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </label>
        <label className="small">
          Hvornår
          <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} required />
        </label>
        <label className="small">
          Hvor
          <input value={location} onChange={(e) => setLocation(e.target.value)} maxLength={200} placeholder="F.eks. mødelokalet" />
        </label>
        <div className="tiny muted">
          Begge får besked og kan forberede sig. Spørgsmålene kopieres ind fra listen nu - en senere rettelse ændrer
          ikke denne samtale.
        </div>
        {error && <div className="error-box">{error}</div>}
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn--primary" disabled={action.isPending}>
            {action.isPending ? 'Gemmer ...' : 'Planlæg'}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

/** Kontorets oversigt: hvem har haft MUS hvornår - aldrig indholdet. */
function OverviewCard({ onPlan }: { onPlan: (employeeId: number) => void }) {
  const { data, isLoading, error } = useMusOverview(true)
  const [showAll, setShowAll] = useState(false)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const rows = data ?? []
  const due = rows.filter((r) => r.isDue)
  const shown = showAll ? rows : due

  return (
    <Card
      title={`Oversigt for kontoret (${due.length} mangler)`}
      actions={
        <button className="btn btn--sm" onClick={() => setShowAll((s) => !s)}>
          {showAll ? 'Kun dem der mangler' : 'Vis alle'}
        </button>
      }
    >
      <div className="tiny muted" style={{ marginBottom: 8 }}>
        Kontoret ser kun hvem, hvornår og status - aldrig forberedelse, referat eller aftaler.
      </div>
      {shown.length === 0 ? (
        <EmptyState>Alle har haft MUS inden for det sidste år, eller har en planlagt.</EmptyState>
      ) : (
        <div className="stack" style={{ gap: 6 }}>
          {shown.map((r) => (
            <div key={r.employee.id} className="row row--between" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div className="small">
                  <strong>{r.employee.displayName}</strong>
                </div>
                <div className="tiny muted">
                  Sidst: {r.lastHeldAt ? formatDate(r.lastHeldAt) : 'aldrig'}
                  {r.next && ` · næste ${formatDateTime(r.next.scheduledAt)} med ${r.next.leader.displayName} (${STATUS[r.next.status].toLowerCase()})`}
                </div>
              </div>
              {r.next ? (
                r.next.canSchedule && <ScheduleButtons summary={r.next} />
              ) : (
                <button className="btn btn--sm" onClick={() => onPlan(r.employee.id)}>
                  Planlæg
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function ScheduleButtons({ summary }: { summary: { id: number; scheduledAt: string; location?: string | null } }) {
  const action = useMusAction()
  const [moving, setMoving] = useState(false)
  const [at, setAt] = useState(toLocalInput(summary.scheduledAt))
  const [error, setError] = useState<string | null>(null)

  async function run(path: string, method: 'post' | 'put', body?: unknown) {
    setError(null)
    try {
      await action.mutateAsync({ path: `/${summary.id}${path}`, method, body })
      setMoving(false)
    } catch (err) {
      setError(errorText(err, 'Det lykkedes ikke.'))
    }
  }

  return (
    <div className="stack" style={{ gap: 4, alignItems: 'flex-end' }}>
      {moving ? (
        <div className="row" style={{ gap: 4 }}>
          <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} style={{ width: 'auto' }} />
          <button
            className="btn btn--sm btn--primary"
            onClick={() => void run('/schedule', 'put', { scheduledAt: new Date(at).toISOString(), location: summary.location ?? null })}
          >
            Gem
          </button>
        </div>
      ) : (
        <div className="row" style={{ gap: 4 }}>
          <button className="btn btn--sm" onClick={() => setMoving(true)}>
            Flyt
          </button>
          <button
            className="btn btn--sm btn--danger"
            onClick={() => {
              if (window.confirm('Aflys samtalen? Begge får besked.')) void run('/cancel', 'post')
            }}
          >
            Aflys
          </button>
        </div>
      )}
      {error && <div className="tiny warn">{error}</div>}
    </div>
  )
}

/** Kontoret retter standardspørgsmålene. Kun nye samtaler får ændringen. */
function QuestionsCard() {
  const [open, setOpen] = useState(false)
  const { data, isLoading } = useMusQuestions(open)
  const action = useMusAction()
  const [draft, setDraft] = useState<(Omit<MusQuestion, 'id' | 'sortOrder'> & { id?: number; key: string })[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const rows = draft ?? (data ?? []).map((q) => ({ ...q, key: `q${q.id}` }))

  function change(key: string, patch: Partial<{ text: string; side: MusSide; isActive: boolean }>) {
    setDraft(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  async function save() {
    setError(null)
    try {
      await action.mutateAsync({
        path: '/questions',
        method: 'put',
        body: { questions: rows.filter((r) => r.text.trim()).map((r) => ({ id: r.id, text: r.text.trim(), side: r.side, isActive: r.isActive })) }
      })
      setDraft(null)
    } catch (err) {
      setError(errorText(err, 'Spørgsmålene kunne ikke gemmes.'))
    }
  }

  return (
    <Card
      title="Spørgsmål til forberedelsen"
      actions={
        <button className="btn btn--sm" onClick={() => setOpen((o) => !o)}>
          {open ? 'Luk' : 'Ret'}
        </button>
      }
    >
      {!open ? (
        <div className="tiny muted">Standardspørgsmålene til medarbejderen og lederen. Kun nye samtaler får en rettelse.</div>
      ) : isLoading ? (
        <Loading />
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          {(['employee', 'leader'] as MusSide[]).map((side) => (
            <div key={side} className="stack" style={{ gap: 6 }}>
              <div className="small">
                <strong>{side === 'employee' ? 'Til medarbejderen' : 'Til lederen'}</strong>
              </div>
              {rows.filter((r) => r.side === side).map((r) => (
                <div key={r.key} className="row" style={{ gap: 6, alignItems: 'center' }}>
                  <input value={r.text} onChange={(e) => change(r.key, { text: e.target.value })} maxLength={500} />
                  <label className="tiny row" style={{ gap: 4, whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      style={{ width: 'auto' }}
                      checked={r.isActive}
                      onChange={(e) => change(r.key, { isActive: e.target.checked })}
                    />
                    Med
                  </label>
                  <button className="btn btn--sm" aria-label="Fjern spørgsmålet" onClick={() => setDraft(rows.filter((x) => x.key !== r.key))}>
                    ✕
                  </button>
                </div>
              ))}
              <div>
                <button
                  className="btn btn--sm"
                  onClick={() => setDraft([...rows, { key: `n${Date.now()}`, text: '', side, isActive: true }])}
                >
                  + Spørgsmål
                </button>
              </div>
            </div>
          ))}
          {error && <div className="error-box">{error}</div>}
          <div>
            <button className="btn btn--primary" disabled={action.isPending || !draft} onClick={() => void save()}>
              Gem spørgsmålene
            </button>
          </div>
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Én samtale - kun for de to
// ---------------------------------------------------------------------------

export function MusPage() {
  const id = Number(useParams().id)
  const navigate = useNavigate()
  const { data: c, isLoading, error } = useMusConversation(id)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!c) return <EmptyState>Samtalen findes ikke, eller den er ikke din.</EmptyState>

  const other = c.mySide === 'employee' ? c.leader : c.employee
  const open = c.status === 'planned' || c.status === 'awaiting_approval'

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          💬 MUS med {other.displayName}
        </h1>
        <button className="btn btn--sm" onClick={() => navigate('/intranet?fane=mus')}>
          Alle samtaler
        </button>
      </div>

      <Card>
        <div className="row row--between" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="stack" style={{ gap: 4 }}>
            <div className="small">
              <strong>{formatDateTime(c.scheduledAt)}</strong>
              {c.location ? ` · ${c.location}` : ''}
            </div>
            <div className="tiny muted">
              {c.employee.displayName} (medarbejder) · {c.leader.displayName} (leder) · {STATUS[c.status]}
            </div>
            <div className="tiny muted">🔒 Kun I to kan se indholdet.</div>
          </div>
          {c.canSchedule && open && <ScheduleButtons summary={c} />}
        </div>
      </Card>

      {c.status !== 'cancelled' && <Preparation c={c} />}
      {c.status !== 'cancelled' && (c.mySide === 'leader' ? <LeaderMinutes c={c} /> : <EmployeeMinutes c={c} />)}
    </div>
  )
}

function Preparation({ c }: { c: MusConversation }) {
  const action = useMusAction()
  const mine = c.answers.filter((a) => a.side === c.mySide)
  const theirs = c.answers.filter((a) => a.side !== c.mySide)
  const [answers, setAnswers] = useState<Record<number, string>>(() =>
    Object.fromEntries(mine.map((a) => [a.id, a.answer ?? '']))
  )
  const [message, setMessage] = useState<string | null>(null)
  const myReady = c.mySide === 'employee' ? c.employeeReadyAt : c.leaderReadyAt
  const theirReady = c.mySide === 'employee' ? c.leaderReadyAt : c.employeeReadyAt
  const otherName = c.mySide === 'employee' ? c.leader.displayName : c.employee.displayName
  const editable = mine.some((a) => a.canEdit)

  async function save(ready: boolean) {
    setMessage(null)
    try {
      await action.mutateAsync({
        path: `/${c.id}/answers`,
        method: 'put',
        body: { answers: mine.map((a) => ({ id: a.id, answer: answers[a.id]?.trim() || null })) }
      })
      if (ready) {
        if (!window.confirm('Tryk "Klar"? Derefter kan din forberedelse ikke rettes.')) {
          setMessage('Gemt.')
          return
        }
        await action.mutateAsync({ path: `/${c.id}/ready` })
      }
      setMessage(ready ? null : 'Gemt.')
    } catch (err) {
      setMessage(errorText(err, 'Det kunne ikke gemmes.'))
    }
  }

  return (
    <Card title="Forberedelse">
      <div className="stack" style={{ gap: 10 }}>
        {mine.length === 0 && <div className="tiny muted">Der er ingen spørgsmål til dig.</div>}
        {mine.map((a) => (
          <label key={a.id} className="small">
            {a.question}
            {editable ? (
              <textarea value={answers[a.id] ?? ''} onChange={(e) => setAnswers((s) => ({ ...s, [a.id]: e.target.value }))} rows={2} />
            ) : (
              <div className="small" style={{ whiteSpace: 'pre-wrap', fontWeight: 400 }}>
                {a.answer || <span className="muted">-</span>}
              </div>
            )}
          </label>
        ))}

        {editable && (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="btn" disabled={action.isPending} onClick={() => void save(false)}>
              Gem
            </button>
            <button className="btn btn--primary" disabled={action.isPending} onClick={() => void save(true)}>
              Klar
            </button>
          </div>
        )}
        {message && <div className="tiny">{message}</div>}

        <div className="tiny muted">
          {myReady ? 'Du er klar.' : 'Du er ikke klar endnu.'} {otherName} er {theirReady ? 'klar' : 'ikke klar endnu'}.
          {!(myReady && theirReady) && ' I ser hinandens svar, når I begge har trykket "Klar".'}
        </div>

        {theirs.length > 0 && (
          <div className="stack" style={{ gap: 8 }}>
            <div className="small">
              <strong>{otherName}s forberedelse</strong>
            </div>
            {theirs.map((a) => (
              <div key={a.id}>
                <div className="small">{a.question}</div>
                <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                  {a.answer || <span className="muted">-</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}

function AgreementList({ c }: { c: MusConversation }) {
  if (c.agreements.length === 0) return null
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="small">
        <strong>Aftaler</strong>
      </div>
      {c.agreements.map((a) => (
        <div key={a.id} className="small">
          {a.taskIsDone ? '✅' : '•'} {a.text}
          <span className="tiny muted">
            {a.assignee ? ` · ${a.assignee.displayName}` : ''}
            {a.dueDate ? ` · frist ${formatDate(a.dueDate)}` : ''}
            {a.taskId ? ' · 🔒 privat opgave' : ''}
          </span>
        </div>
      ))}
    </div>
  )
}

function LeaderMinutes({ c }: { c: MusConversation }) {
  const action = useMusAction()
  const [minutes, setMinutes] = useState(c.minutes ?? '')
  const [agreements, setAgreements] = useState(() =>
    c.agreements.map((a) => ({
      key: `a${a.id}`,
      text: a.text,
      assignee: a.assignee ? String(a.assignee.id) : '',
      due: a.dueDate ? a.dueDate.slice(0, 10) : ''
    }))
  )
  const [message, setMessage] = useState<string | null>(null)
  const held = new Date(c.scheduledAt) <= new Date()
  const locked = c.status === 'approved'

  if (locked) {
    return (
      <Card title="Referat - godkendt">
        <div className="stack" style={{ gap: 8 }}>
          <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{c.minutes}</div>
          <AgreementList c={c} />
          {c.employeeComment && <div className="small">💬 {c.employee.displayName}: {c.employeeComment}</div>}
          <div className="tiny muted">Godkendt {formatDateTime(c.approvedAt)}</div>
        </div>
      </Card>
    )
  }

  function update(key: string, patch: Partial<{ text: string; assignee: string; due: string }>) {
    setAgreements((all) => all.map((a) => (a.key === key ? { ...a, ...patch } : a)))
  }

  async function save(share: boolean) {
    setMessage(null)
    try {
      await action.mutateAsync({
        path: `/${c.id}/minutes`,
        method: 'put',
        body: {
          minutes: minutes.trim() || null,
          agreements: agreements
            .filter((a) => a.text.trim())
            .map((a) => ({ text: a.text.trim(), assigneeUserId: a.assignee ? Number(a.assignee) : null, dueDate: a.due || null }))
        }
      })
      if (share) {
        await action.mutateAsync({ path: `/${c.id}/minutes/share` })
      }
      setMessage(share ? `Sendt til ${c.employee.displayName}.` : 'Gemt - kun du kan se kladden.')
    } catch (err) {
      setMessage(errorText(err, 'Referatet kunne ikke gemmes.'))
    }
  }

  return (
    <Card title={c.minutesSharedAt ? 'Referat - sendt til godkendelse' : 'Referat'}>
      <div className="stack" style={{ gap: 10 }}>
        <div className="tiny muted">
          Du skriver referatet. {c.employee.displayName} ser det først, når du sender det, og godkender det så.
        </div>
        <textarea value={minutes} onChange={(e) => setMinutes(e.target.value)} rows={6} placeholder="Hvad talte I om?" />

        <div className="small">
          <strong>Aftaler</strong>
          <div className="tiny muted">Med en ansvarlig bliver aftalen en privat opgave, når referatet er godkendt.</div>
        </div>
        {agreements.map((a) => (
          <div key={a.key} className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <input value={a.text} onChange={(e) => update(a.key, { text: e.target.value })} placeholder="Aftalen" maxLength={500} style={{ flex: '1 1 200px' }} />
            <select value={a.assignee} onChange={(e) => update(a.key, { assignee: e.target.value })} aria-label="Ansvarlig" style={{ width: 'auto' }}>
              <option value="">Ingen ansvarlig</option>
              <option value={c.employee.id}>{c.employee.displayName}</option>
              <option value={c.leader.id}>{c.leader.displayName}</option>
            </select>
            <input type="date" value={a.due} disabled={!a.assignee} onChange={(e) => update(a.key, { due: e.target.value })} aria-label="Frist" style={{ width: 'auto' }} />
            <button className="btn btn--sm" onClick={() => setAgreements((all) => all.filter((x) => x.key !== a.key))}>
              ✕
            </button>
          </div>
        ))}
        <div>
          <button className="btn btn--sm" onClick={() => setAgreements((all) => [...all, { key: `n${Date.now()}`, text: '', assignee: '', due: '' }])}>
            + Aftale
          </button>
        </div>

        {message && <div className="tiny">{message}</div>}
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" disabled={action.isPending} onClick={() => void save(false)}>
            Gem kladden
          </button>
          <button
            className="btn btn--primary"
            disabled={action.isPending || !held || !minutes.trim()}
            title={held ? undefined : 'Referatet kan først sendes, når samtalen er holdt.'}
            onClick={() => void save(true)}
          >
            {c.minutesSharedAt ? 'Send igen' : `Send til ${c.employee.displayName}`}
          </button>
        </div>
      </div>
    </Card>
  )
}

function EmployeeMinutes({ c }: { c: MusConversation }) {
  const action = useMusAction()
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (!c.minutesSharedAt) {
    return (
      <Card title="Referat">
        <div className="tiny muted">{c.leader.displayName} skriver referatet efter samtalen. Du får besked, når det er klar.</div>
      </Card>
    )
  }

  async function approve() {
    setError(null)
    if (!window.confirm('Godkend referatet? Derefter kan det ikke rettes.')) return
    try {
      await action.mutateAsync({ path: `/${c.id}/approve`, body: { comment: comment.trim() || null } })
    } catch (err) {
      setError(errorText(err, 'Referatet kunne ikke godkendes.'))
    }
  }

  return (
    <Card title={c.approvedAt ? 'Referat - godkendt' : 'Referat - læs og godkend'}>
      <div className="stack" style={{ gap: 8 }}>
        <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{c.minutes}</div>
        <AgreementList c={c} />
        {c.approvedAt ? (
          <>
            {c.employeeComment && <div className="small">💬 Din kommentar: {c.employeeComment}</div>}
            <div className="tiny muted">Godkendt {formatDateTime(c.approvedAt)}</div>
          </>
        ) : (
          <>
            <label className="small">
              Kommentar (valgfri)
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} maxLength={2000} />
            </label>
            {error && <div className="error-box">{error}</div>}
            <div>
              <button className="btn btn--primary" disabled={action.isPending} onClick={() => void approve()}>
                Godkend referatet
              </button>
            </div>
            <div className="tiny muted">Er der noget, der ikke passer, så skriv det i kommentaren - eller tal med {c.leader.displayName} først.</div>
          </>
        )}
      </div>
    </Card>
  )
}
